/// <amd-module name="Wit/UI/Components/WorkItemGrid/WorkItemGrid" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { dateToString } from "VSS/Core/Util/String";
import { Intersection } from "VSSUI/Intersection";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ColumnFill, ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { DEFAULT_WORKITEM_PAGE_SIZE, ItemsSourceType, PagedWorkItemsList, WorkItemStateProvider } from "Wit/UI/Data";
import { IdentityDisplayMode } from "Wit/UI/Enums";
import { IdentityView } from "Wit/UI/IdentityView";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { WorkItemSmallView } from "Wit/UI/WorkItemViews";
import { PagedWorkItemsProvider } from "../../ItemProviders/PagedWorkItemsProvider";
import { IWorkItemGridColumn, IWorkItemGridProps } from "./WorkItemGrid.props";

export type WorkItemGridItem = {
    data: WorkItem | null;
};
export interface IWorkItemGridState {
    areIdsResolved: boolean;
}

/**
 * Component to display a list of work items.
 */
export class WorkItemGrid extends React.Component<IWorkItemGridProps, IWorkItemGridState> {

    private static mapWorkItems(workItems: readonly (WorkItem | null)[] | null): (ObservableValue<WorkItemGridItem | undefined> | WorkItemGridItem)[] {
        return workItems
            ? workItems.map(item => {
                if (!item) {
                    return new ObservableValue<WorkItemGridItem | undefined>(undefined);
                } else {
                    return { data: item };
                }
            })
            : [];
    }
    private itemProvider: PagedWorkItemsProvider<WorkItemGridItem>;
    private columns: ITableColumn<WorkItemGridItem>[];

    constructor(props: IWorkItemGridProps) {
        super(props);

        this.state = this._getStateFromProps(this.props);

        this._initialize(this.props);
    }

    public render(): JSX.Element {
        const { className, containerElement } = this.props;

        let content: JSX.Element;
        if (!this.state.areIdsResolved) {
            // the items haven't been loaded yet, and the length is 0 which means the item source is wiql
            content = <Spinner className="work-item-loading-spinner-overlay" size={SpinnerSize.large} />;
        } else {
            // Intersection requires a pure dom element before a jsx element which is why
            // there is that random div.
            content = (
                <Intersection rootElement={containerElement ? containerElement : undefined}>
                    <div>
                        <Table<WorkItemGridItem>
                            className={css("work-item-grid-content", className)}
                            columns={this.columns}
                            itemProvider={this.itemProvider}
                            singleClickActivation={false}
                        />
                    </div>
                </Intersection>
            );
        }

        return <div className="wit-ui-control work-item-grid">{content}</div>;
    }

    public componentDidUpdate(prevProps: IWorkItemGridProps) {
        if (this._didPropsChanged(prevProps, this.props)) {
            this._initialize(this.props);
            this.setState(this._getStateFromProps(this.props));
        }
    }

    /**
     * When we get new props, we need to re-initialize the columns and the provider
     * @param props props
     */
    private _initialize(props: IWorkItemGridProps): void {
        const { columns } = props;

        this.columns = this._buildColumns(columns);
        this._initializePagedWorkItemsProvider(props);
    }

    private _didPropsChanged(prevProps: IWorkItemGridProps, currentProps: IWorkItemGridProps): boolean {
        return prevProps.itemsSource !== currentProps.itemsSource || prevProps.dataProvider !== currentProps.dataProvider;
    }

    private _getStateFromProps(props: IWorkItemGridProps): IWorkItemGridState {
        return {
            // in all cases except wiql ids should be provided by the consumer
            areIdsResolved: PagedWorkItemsList.getItemsSourceType(props.itemsSource) !== ItemsSourceType.Wiql
        };
    }

    private async _initializePagedWorkItemsProvider(props: IWorkItemGridProps): Promise<void> {
        const { itemsSource, dataProvider } = props;

        const pageSize = DEFAULT_WORKITEM_PAGE_SIZE;
        const pagedWorkItemsList = dataProvider.getPagedWorkItemsList(itemsSource, pageSize);
        this.itemProvider = new PagedWorkItemsProvider<WorkItemGridItem>(itemsSource, WorkItemGrid.mapWorkItems, pagedWorkItemsList, pageSize);

        // preload the first page if it's not already available
        if (!this.itemProvider.isFirstPageReady()) {
            await this.itemProvider.ensureFirstPage();
            // if this was a wiql query, areIdsResolved would have been false since the ids are
            // resolved when ensuring the first page.  once we ensure the first page we know
            // that the ids will be populated in all cases
            this.setState({
                areIdsResolved: true
            });
        }
    }

    private _buildColumns(workItemGridColumns: IWorkItemGridColumn[]): ITableColumn<WorkItemGridItem>[] {
        const columns = workItemGridColumns.map(this._buildColumn);
        // To ensure the table will take the full width of the available space, we need a small non fixed sized
        // column at the end.
        columns.push({
            ...ColumnFill,
            width: -0.01
        });

        // Find the column with the highest max width, and set it's
        // width to -100.  This will simulate that column to grow to
        // fill all available space.
        let foundMaxWidth = false;
        let maxWidthIndex = 0;
        let maxWidth = 0;
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            if (column.maxWidth! > maxWidth) {
                foundMaxWidth = true;
                maxWidth = column.maxWidth!;
                maxWidthIndex = i;
            }
        }

        if (foundMaxWidth) {
            if (workItemGridColumns[maxWidthIndex].isResizable) {
                (columns[maxWidthIndex].width as ObservableValue<number>).value = -100;
            } else {
                columns[maxWidthIndex].width = -100;
            }
        }

        return columns;
    };

    private readonly _buildColumn = (workItemGridColumn: IWorkItemGridColumn): ITableColumn<WorkItemGridItem> => {
        const { name, fieldName, minWidth, maxWidth, ariaLabel, isResizable, onRender } = workItemGridColumn;

        const cellRenderer = (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITableColumn<WorkItemGridItem>,
            tableItem: WorkItemGridItem
        ): JSX.Element => {
            if (!tableItem || !tableItem.data) {
                return null!;
            }

            const cellRenderer = onRender
                ? onRender(tableItem.data, columnIndex, workItemGridColumn)
                : this._defaultCellRenderer(tableItem.data, workItemGridColumn.fieldName)!;

            return (
                <SimpleTableCell
                    className={"work-item-grid-column-cell"}
                    tableColumn={tableColumn}
                    columnIndex={columnIndex}
                    key={"col-" + columnIndex}
                >
                    {cellRenderer}
                </SimpleTableCell>
            );
        };

        const onSize = (event: MouseEvent, columnIndex: number, width: number, column: ITableColumn<WorkItemGridItem>): void => {
            (this.columns[columnIndex].width as ObservableValue<number>).value = width;
        };

        return {
            id: "col-" + fieldName,
            name: name || fieldName,
            renderCell: cellRenderer,
            minWidth: minWidth,
            maxWidth: maxWidth,
            width: isResizable ? new ObservableValue(minWidth || maxWidth) : minWidth || maxWidth,
            onSize: isResizable ? onSize : undefined
        } as ITableColumn<WorkItemGridItem>;
    };

    private _defaultCellRenderer(workItem: WorkItem | null, fieldName: string): JSX.Element | null {
        if (!workItem) {
            return null;
        }

        const { dataProvider } = this.props;

        let cellContent: JSX.Element | null = null;
        let fieldValue = workItem.fields[fieldName];
        if (fieldName === CoreFieldRefNames.Title) {
            cellContent = (
                <WorkItemSmallView
                    dataProvider={dataProvider}
                    data={workItem}
                    showId={false}
                    showTitleLink={true}
                    onTitleLinkClick={this.props.onTitleLinkClick}
                />
            );
        } else if (fieldName === CoreFieldRefNames.State) {
            cellContent = (
                <WorkItemStateProvider dataProvider={dataProvider} dataSource={workItem}>
                    {stateAndColor => <WorkItemState data={stateAndColor} hideText={false} />}
                </WorkItemStateProvider>
            );
        } else if (fieldName === CoreFieldRefNames.Tags) {
            const tagsString = workItem.fields[fieldName] as string;
            if (tagsString) {
                const tags = tagsString.split(";").map(item => item.trim());
                cellContent = <TagsList className={"work-item-grid-tags"} tags={tags} />;
            }
        } else if (fieldValue && fieldValue.displayName !== undefined) {
            cellContent = <IdentityView identityRef={fieldValue} viewMode={IdentityDisplayMode.NameOnly} displayTooltipOnHover={true} />;
        } else {
            if (fieldName === CoreFieldRefNames.Id) {
                fieldValue = workItem.id;
            } else if (fieldValue instanceof Date) {
                fieldValue = dateToString(fieldValue, true);
            }
            cellContent = (
                <Tooltip overflowOnly={true} text={fieldValue}>
                    <div className="text-ellipsis">{fieldValue || ""}</div>
                </Tooltip>
            );
        }

        return cellContent;
    }
}
